import { VFC } from "react";

import { Add } from "@material-ui/icons";
import { NumberInput, portexColor } from "@portex-pro/ui-components";
import { LoadStatus, LoadStatusEnum, LoadStatusUpdate } from "api/rest/load-status-updates/types";
import getMetaForStatusUpdate from "api/rest/load-status-updates/utils/getMetaForStatusUpdate";
import { BrokerShipmentStopDetails } from "api/rest/shipments/types";
import ButtonSelectView from "components/ButtonSelectView";
import NotificationBadge from "components/NotificationBadge";
import StatusView from "components/StatusView";
import { useGetNotificationDataFromId } from "hooks/notifications/useGetNotificationDataFromId";
import clamp from "lodash/clamp";
import round from "lodash/round";
import { useTranslation } from "react-i18next";
import { PortexSocket } from "sockets/PortexSocket";

import LoadStatusView from "../LoadStatusView";

interface StatusColumnCellViewProps {
  stop: BrokerShipmentStopDetails;
  totalStops: number;
  loadStatusUpdate: LoadStatusUpdate | null;
  meta: ReturnType<typeof getMetaForStatusUpdate>;
  loadId: number | undefined;
  onChangeStatus?: (stopId: number, val: LoadStatus) => void;
  showScore?: boolean;
  onChangeScore?: (stopId: number, val: number) => void;
}

const StatusColumnCellView: VFC<StatusColumnCellViewProps> = ({
  stop,
  totalStops,
  loadStatusUpdate,
  meta,
  loadId,
  onChangeStatus,
  showScore,
  onChangeScore,
}) => {
  const error = meta.errors.includes("Missing Status");
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const notificationData = useGetNotificationDataFromId(`loadStatusShipmentStop:${loadId}-${stop.id}`);
  const { t } = useTranslation("loads");

  const handleClearNotifications = () => {
    notificationData?.forEach((notif) => {
      PortexSocket.userSocket.emit("notification-seen", { id: notif.id });
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-start">
        <NotificationBadge
          identifiers={[`loadStatusShipmentStop:${loadId}-${stop.id}`]}
          position="topLeft"
          size="small"
        >
          <ButtonSelectView
            value={loadStatusUpdate?.status ?? undefined}
            buttonProps={{
              color: "default",
              variant: "outlined",
              size: "medium",
              style: {
                padding: 0,
                borderWidth: "2px",
                borderRadius: 16,
                borderColor: error ? portexColor.red500 : "transparent",
              },
            }}
            placeholder={
              <StatusView pill className="bg-brandBlue-overlay text-brandBlue items-center capitalize">
                <Add style={{ fontSize: "initial", marginTop: -2 }} /> {t("statusSelect_placeholder")}
              </StatusView>
            }
            items={Object.values(LoadStatusEnum).map((value) => ({
              value,
              label: (
                <LoadStatusView
                  statusProps={{ pill: true }}
                  status={value}
                  meta={{ stopPosition: stop.position, totalStops: totalStops }}
                />
              ),
            }))}
            onChange={(value) => {
              handleClearNotifications();
              onChangeStatus?.(stop.id, value);
            }}
          />
        </NotificationBadge>
      </div>
      {!!showScore && !!onChangeScore && (
        <div className="text-right">
          <NumberInput
            label={t("scoreInputLabel")}
            placeholder={t("scoreInputPlaceholder")}
            InputProps={{
              endAdornment: <span className="text-grey-700">{t("scoreInputEndAdornment")}</span>,
              style: { width: 100, alignSelf: "flex-end" },
            }}
            value={loadStatusUpdate?.score}
            onChange={(event) => onChangeScore?.(stop.id, round(clamp(Number(event.target.value), 0, 100), 0))}
            displayZero
            validator={(value) => {
              const numberValue = Number(value);

              const result = {
                isValid: true,
                numberValue,
                error: "",
              };

              if (numberValue >= 0 && numberValue <= 100) {
                result.isValid = true;
              } else {
                result.isValid = false;
                result.error = t("scoreInputError");
              }

              return result;
            }}
          />
        </div>
      )}
    </div>
  );
};

export default StatusColumnCellView;
